import { ActionError, Button, cn } from '@westhetics/ui'
import { useId, useState, type FormEvent } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router'
import { PublicScreen, publicTextAction } from '@/app/public-screen'
import { noteText, supportText } from '@/app/shell'
import { errorMessage, fieldErrors } from '@/lib/errors'
import { useSession } from '@/lib/session'
import { routes } from '@/routes'
import {
  SIGN_IN_PATHS,
  useIdentityCode,
  useIdentityLookup,
  useIdentityVerify,
  type IdentityDocument,
  type IdentityOptions,
} from '../identity/api'
import { CodePanel } from '../identity/code-panel'
import {
  EMPTY_DOCUMENT,
  toIdentityDocument,
  validateDocument,
  type DocumentValues,
} from '../identity/document'
import { DocumentFields } from '../identity/document-fields'
import type { VerifyResult } from './api'

/**
 * Entrar (docs/areas/portal-do-paciente.md, "Entrar"; RN-POR-01, RN-POR-19, RN-POR-22; T59).
 * Arquétipo: fluxo em etapas, no celular. Pede o documento (CPF ou o estrangeiro), mostra os e-mails
 * mascarados dos cadastros com ele para a pessoa escolher onde o código chega, e pede o código de 6
 * dígitos. A sessão cobre os cadastros com o documento e o e-mail confirmado. Com o código certo,
 * guarda o token e vai ao início.
 */

const MESSAGES = {
  expired: 'Sua sessão terminou. Entre de novo com o seu documento.',
  hint: 'Use o documento que você deu à clínica. O código chega num dos e-mails do seu cadastro.',
} as const

const TITLE = 'Seus atendimentos, documentos e fichas de saúde.'

export function SignInPage() {
  const session = useSession()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const id = useId()
  const lookup = useIdentityLookup(SIGN_IN_PATHS)
  const sendCode = useIdentityCode(SIGN_IN_PATHS)
  const verify = useIdentityVerify<VerifyResult>(SIGN_IN_PATHS)
  const [values, setValues] = useState<DocumentValues>(EMPTY_DOCUMENT)
  const [attempted, setAttempted] = useState(false)
  const [found, setFound] = useState<{
    document: IdentityDocument
    options: IdentityOptions
  } | null>(null)

  // Quem já entrou não tem o que fazer aqui.
  if (session.status === 'signed_in') return <Navigate to={routes.home} replace />

  const expired = params.get('reason') === 'expired' || session.reason === 'expired'
  const apiErrors = fieldErrors(lookup.error)
  const errors = attempted
    ? {
        ...validateDocument(values),
        ...(apiErrors['document.number'] ? { docNumber: apiErrors['document.number'] } : {}),
      }
    : {}
  const lookupError =
    lookup.isError && Object.keys(apiErrors).length === 0 ? errorMessage(lookup.error) : null

  function submitDocument(event: FormEvent) {
    event.preventDefault()
    setAttempted(true)
    const document = toIdentityDocument(values)
    if (!document || lookup.isPending) return
    lookup.mutate(document, { onSuccess: (options) => setFound({ document, options }) })
  }

  function restart() {
    setFound(null)
    lookup.reset()
    sendCode.reset()
    verify.reset()
  }

  if (!found) {
    return (
      <PublicScreen eyebrow="Portal do paciente" title={TITLE} onSubmit={submitDocument}>
        <p className={cn(supportText, 'mt-3')}>
          Informe o seu documento. Enviamos um código de 6 dígitos para um dos e-mails do seu
          cadastro, sem senha.
        </p>
        {expired ? (
          <ActionError className="mt-6" title="Entre de novo" description={MESSAGES.expired} />
        ) : null}

        <DocumentFields
          values={values}
          onChange={(next) => {
            setValues(next)
            lookup.reset()
          }}
          errors={errors}
          idPrefix={id}
        />
        <p className={cn(noteText, 'mt-3')}>{MESSAGES.hint}</p>

        {lookupError ? (
          <ActionError
            className="mt-6"
            title="Não foi possível procurar o seu cadastro"
            description={lookupError}
          />
        ) : null}

        <Button
          type="submit"
          size="lg"
          className="mt-7"
          loading={lookup.isPending}
          loadingLabel="Procurando…"
        >
          Continuar
        </Button>
      </PublicScreen>
    )
  }

  const { document, options } = found
  return (
    <PublicScreen eyebrow="Portal do paciente" title={TITLE}>
      {options.emails.length === 0 ? (
        <p className={cn(supportText, 'mt-3')}>{options.message}</p>
      ) : (
        <CodePanel
          className="mt-3"
          options={options.emails}
          onSend={(emailId) => sendCode.mutateAsync({ document, emailId })}
          onConfirm={async (emailId, code) => {
            const result = await verify.mutateAsync({ document, emailId, code })
            session.signIn(result.token)
            navigate(routes.home, { replace: true })
          }}
          confirmLabel="Entrar"
          confirmingLabel="Entrando…"
        />
      )}
      <button type="button" onClick={restart} className={cn(publicTextAction, 'mt-6')}>
        Usar outro documento
      </button>
    </PublicScreen>
  )
}
